/**
 * The payout (Phase 8). What a run was actually worth, settled when it ends.
 *
 * The take is just the value of what you carried out. The BONUSES are where the
 * scoring says what kind of game this is: the biggest one is for nobody ever
 * seeing you, the next for leaving no bodies. A run where you shot your way to
 * the safe and back pays the loot and nothing else — which is the difference
 * between succeeding and doing the job properly.
 *
 * Pure — takes a snapshot, returns numbers. No world access.
 */

import { CONFIG } from '../config';
import type { Item } from './inventory';

export interface ScoreInput {
  /** Everything in the player's hands at the end. */
  loot: readonly Item[];
  /** The contract was completed (not just survived). */
  completed: boolean;
  /** No guard, civilian or camera ever got eyes on the player. */
  neverSeen: boolean;
  /** Nobody died — guards, civilians, the janitor, the client. */
  noKills: boolean;
  /** The extraction target walked out with you (undefined when there isn't one). */
  clientSafe?: boolean;
}

export interface ScoreLine {
  label: string;
  amount: number;
}

export interface Score {
  take: number;
  bonuses: ScoreLine[];
  total: number;
}

/** Settle a run. */
export function scoreMission(input: ScoreInput): Score {
  const cfg = CONFIG.score;
  const take = input.loot.reduce((sum, it) => sum + (it.value ?? 0), 0);
  const bonuses: ScoreLine[] = [];
  if (input.completed) bonuses.push({ label: 'Contract fulfilled', amount: cfg.contract });
  // Ghost outranks pacifist: not being there at all is the harder discipline.
  if (input.neverSeen) bonuses.push({ label: 'Never seen', amount: cfg.ghost });
  if (input.noKills) bonuses.push({ label: 'No casualties', amount: cfg.pacifist });
  if (input.clientSafe === true) bonuses.push({ label: 'Client extracted', amount: cfg.client });
  return {
    take,
    bonuses,
    total: take + bonuses.reduce((sum, b) => sum + b.amount, 0),
  };
}

/** "$12,400" — the one place the number becomes a string. */
export function formatMoney(n: number): string {
  return `$${Math.round(n).toLocaleString('en-US')}`;
}
